import { Features } from '@ardoq/api-types';
import { PageWrapper } from '@ardoq/page-layout';
import {
  IndexRouteContent,
  LayoutRouteContent,
  PathRouteContent,
  RouteNode,
} from 'router/declarative/routeNode';
import { AppModules } from '../appContainer/types';
import { adminBoundary } from '../router/declarative/adminBoundary';
import { composeBoundaries } from '../router/declarative/composeBoundaries';
import { fixedResourcePermissionBoundary } from '../router/declarative/declarativePermissionBoundary';
import {
  disabledByFeatureFlagBoundary,
  featureFlagBoundary,
} from '../router/declarative/featureFlagBoundary';
import { filterParam } from '../router/declarative/filterParam';
import {
  param,
  sortingParam,
  uriComponentParam,
} from '../router/declarative/paramRegistry';

const groupByParam = param<string[]>({
  fromString: value => value.split(',').map(decodeURIComponent),
  toString: value => value.map(encodeURIComponent).join(','),
});

const stringParam = param({
  fromString: value => value,
  toString: value => value,
});

export const routes = {
  path: '/organization-metamodel',
  title: 'Metamodel',
  appModule: AppModules.ORGANIZATION_METAMODEL,
  content: async () => PageWrapper,
  children: [
    {
      path: { layout: true },
      content: adminBoundary(),
      children: [
        {
          path: { layout: true },
          content: async (): LayoutRouteContent =>
            (await import('./PageLayout')).PageLayout,
          children: [
            {
              path: { index: true },
              content: async (): IndexRouteContent =>
                (await import('./overview/MetamodelOverviewPage'))
                  .MetamodelOverviewPage,
            },
            {
              path: { layout: true },
              content: composeBoundaries([
                fixedResourcePermissionBoundary(
                  {
                    pageName: 'Fields',
                    message: 'You do not have access to this page.',
                  },
                  'organization/manage_global_fields',
                  'organization'
                ),
                async (): LayoutRouteContent =>
                  (await import('./PageLayout')).FieldsSectionLayout,
              ]),
              children: [
                {
                  path: '/fields',
                  searchParams: {
                    query: uriComponentParam,
                    filter: filterParam,
                    componentType: uriComponentParam,
                    workspace: uriComponentParam,
                  },
                  content: async (): PathRouteContent =>
                    (await import('./globalFields/GlobalFieldsRouteContent'))
                      .GlobalFieldsRouteContent,
                  children: [
                    {
                      path: { layout: true },
                      content: disabledByFeatureFlagBoundary(
                        [Features.BLOCK_TYPE_AND_FIELD_CREATION],
                        { pageName: 'Fields' }
                      ),
                      children: [
                        {
                          path: '/create',
                          content: async (): PathRouteContent =>
                            (
                              await import('./globalFields/create/CreateGlobalFieldSidebarPage')
                            ).CreateGlobalFieldSidebarPage,
                        },
                      ],
                    },
                    {
                      path: '/:entityId',
                      children: [
                        {
                          path: { index: true },
                          content: async (): IndexRouteContent =>
                            (
                              await import('./globalFields/sidebar/GlobalFieldSidebar')
                            ).GlobalFieldsSidebar,
                        },
                        {
                          path: { layout: true },
                          content: async (): LayoutRouteContent =>
                            (
                              await import('./globalFields/fieldPage/FieldPageLayout')
                            ).FieldPageLayout,
                          children: [
                            {
                              path: '/overview',
                              content: async (): PathRouteContent =>
                                (
                                  await import('./globalFields/fieldPage/FieldOverviewPage')
                                ).FieldOverviewPage,
                            },
                            {
                              path: '/usage',
                              content: async (): PathRouteContent =>
                                (
                                  await import('./globalFields/fieldPage/FieldUsagePage')
                                ).FieldUsagePage,
                            },
                            {
                              path: '/groups',
                              content: async (): PathRouteContent =>
                                (
                                  await import('./globalFields/fieldPage/GroupsPage')
                                ).GroupsPage,
                            },
                            {
                              path: '/rename',
                              content: async (): PathRouteContent =>
                                (
                                  await import('./globalFields/fieldPage/RenamePage')
                                ).RenamePage,
                            },
                          ],
                        },
                      ],
                    },
                  ],
                },
                {
                  path: '/field-groups',
                  content: async (): PathRouteContent =>
                    (await import('../fieldGroups/FieldGroupsPage'))
                      .FieldGroupsPage,
                  children: [
                    {
                      path: '/create',
                      content: async (): PathRouteContent =>
                        (
                          await import('../fieldGroups/create/CreateFieldGroupSidebarPage')
                        ).CreateFieldGroupSidebarPage,
                    },
                    {
                      path: '/:entityId',
                      content: async (): PathRouteContent =>
                        (
                          await import('../fieldGroups/sidebar/FieldGroupSidebar')
                        ).FieldGroupSidebar,
                    },
                  ],
                },
              ],
            },
            {
              path: { layout: true },
              content: fixedResourcePermissionBoundary(
                { pageName: 'Metamodel Constraints' },
                'organization/manage_metamodel_constraints',
                'organization'
              ),
              children: [
                {
                  path: '/solutions-graph',
                  content: async (): PathRouteContent =>
                    (await import('./triples/ConstraintsGraphPage'))
                      .ConstraintsGraphPage,
                },
                {
                  path: '/metamodel-graph',
                  content: async (): PathRouteContent =>
                    (await import('./metamodelGraph/MetamodelGraphLayout'))
                      .MetamodelGraphLayout,
                  children: [
                    {
                      path: { index: true },
                      redirect: {
                        route:
                          '/organization-metamodel/metamodel-graph/preview',
                      },
                    },
                    {
                      path: '/preview',
                      content: async (): PathRouteContent =>
                        (
                          await import('./metamodelGraph/preview/MetamodelPreviewPage')
                        ).MetamodelPreviewPage,
                    },
                    {
                      path: '/diff',
                      content: async (): PathRouteContent =>
                        (
                          await import('./metamodelGraph/diff/MetamodelDiffPage')
                        ).MetamodelDiffPage,
                      children: [
                        {
                          path: '/:metamodelGraphElement',
                          content: async (): PathRouteContent =>
                            (
                              await import('./metamodelGraph/diff/sidebar/MetamodelGraphSidebar')
                            ).MetamodelGraphSidebar,
                        },
                      ],
                    },
                  ],
                },
                {
                  path: { layout: true },
                  content: async (): LayoutRouteContent =>
                    (await import('./ConstraintsLayout')).ConstraintsLayout,
                  children: [
                    {
                      searchParams: {
                        groupBy: groupByParam,
                        sorting: sortingParam,
                        filter: filterParam,
                      },
                      path: '/constraints',
                      content: async () =>
                        (await import('./triples/TriplesPage')).TriplesPage,
                      children: [
                        {
                          path: '/new',
                          searchParams: {
                            sourceComponentType: stringParam,
                            referenceType: stringParam,
                            targetComponentType: stringParam,
                          },
                          content: async (): PathRouteContent =>
                            (
                              await import('./triples/newTriple/NewTripleSidebarPage')
                            ).NewTripleSidebarPage,
                        },
                        {
                          path: '/:tripleId',
                          content: async (): PathRouteContent =>
                            (
                              await import('./triples/editTriple/EditTripleSidebarPage')
                            ).EditTripleSidebarPage,
                        },
                      ],
                    },
                    {
                      path: '/implicit',
                      content: async (): PathRouteContent =>
                        (await import('./implicitTypes/ImplicitTypesPage'))
                          .ImplicitTypesPage,
                    },
                  ],
                },
              ],
            },
            {
              path: { layout: true },
              content: featureFlagBoundary(
                [Features.CORE_TMP_GLOBAL_WORKSPACE_TABLE],
                {
                  pageName: 'Workspaces',
                }
              ),
              children: [
                {
                  path: '/workspaces',
                  searchParams: {
                    query: uriComponentParam,
                    sorting: sortingParam,
                  },
                  content: async (): PathRouteContent =>
                    (await import('./workspaces/WorkspacesPage'))
                      .WorkspacesPage,
                },
              ],
            },
            {
              path: { layout: true },
              content: composeBoundaries([
                fixedResourcePermissionBoundary(
                  { pageName: 'Component Types' },
                  'organization/manage_metamodel_constraints',
                  'organization'
                ),
                featureFlagBoundary([Features.GLOBAL_COMPONENT_TYPES_BETA], {
                  pageName: 'Component Types',
                }),
              ]),
              children: [
                {
                  path: '/component-types',
                  searchParams: { query: uriComponentParam },
                  content: async (): PathRouteContent =>
                    (await import('./componentTypes/ComponentTypesPage'))
                      .ComponentTypesPage,
                  children: [
                    {
                      path: { layout: true },
                      content: disabledByFeatureFlagBoundary(
                        [Features.BLOCK_TYPE_AND_FIELD_CREATION],
                        { pageName: 'Component Types' }
                      ),
                      children: [
                        {
                          path: '/create',
                          content: async (): PathRouteContent =>
                            (
                              await import('./componentTypes/create/CreateComponentTypeSidebarPage')
                            ).CreateComponentTypeSidebarPage,
                        },
                      ],
                    },
                  ],
                },
              ],
            },
          ],
        },
      ],
    },
  ],
} as const satisfies RouteNode;
